import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { exportStatistics } from './statisticsExport/exportStatistics';
import type { StatisticSectionRow } from './StatisticsSectionsTable/types';

interface StatisticsExportOptions {
  readonly direction: 'outbound' | 'inbound';
  readonly sections: readonly StatisticSectionRow[];
  readonly request: { readonly From: string; readonly To: string };
  readonly ready: boolean;
}

export const useStatisticsExport = ({
  direction,
  sections,
  request,
  ready,
}: StatisticsExportOptions) => {
  const [sectionModes, setSectionModes] = useState<Record<string, string>>({});
  const [isReportLoading, setReportLoading] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleSectionModeChange = useCallback((key: string, mode: string) => {
    setSectionModes((previous) => ({ ...previous, [key]: mode }));
  }, []);

  const canExport = ready && sections.length > 0 && !isReportLoading;
  const handleReport = useCallback(async (): Promise<void> => {
    if (!canExport || pending.current) return;
    pending.current = true;
    setReportLoading(true);
    try {
      await exportStatistics({
        direction,
        period: { from: request.From, to: request.To },
        sections,
        modes: sectionModes,
      });
    } catch {
      notification.error({
        message: 'Не удалось сформировать отчет',
        description: 'Повторите попытку формирования отчета.',
      });
    } finally {
      pending.current = false;
      if (mounted.current) setReportLoading(false);
    }
  }, [canExport, direction, request.From, request.To, sections, sectionModes]);

  return {
    sectionModes,
    handleSectionModeChange,
    handleReport,
    isReportLoading,
    canExport,
  };
};
